import { useTranslation } from 'react-i18next';
import { useState, SyntheticEvent, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import { axiosEstimates } from '../../utils/api';
import { socket } from '../../utils/sockets.util';
import { SupplierChat, ChatsObj, Config } from '../../models';
import { CustomFiles } from '../../models/CustomFiles';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/reducers';
import { Lan } from 'mdi-material-ui';

export function useSupplierChat() {
  // ** State
  // const [token, setToken] = useState<string | null>(null);
  const [idBudget, setIdBudget] = useState<number | null>(null);
  const [chat, setChat] = useState<ChatsObj | null>(null);
  const [idUsers, setIdUsers] = useState<number[] | null>(null);
  const [idSupplier, setIdSupplier] = useState<number | null>(null);
  const [config, setConfig] = useState<Config | null>(null);
  const [msg, setMsg] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const [file, setFile] = useState<CustomFiles>(null);
  const [openFileUploader, setOpenFileUploader] = useState(false);
  const {
    user: { idCRM },
  } = useSelector((state: RootState) => state);

  const { t, i18n } = useTranslation('common');

  const handleToggle = () => {
    setOpen(prev => !prev);
  };

  // Extraer idUser únicos directamente del supplier
  const getUserIdsWithMessage = (supplier: SupplierChat, messageContent: string): number[] => {
    if (!supplier?.supplierChats) return [];

    const userIds = supplier.supplierChats
      .filter(chat => chat.content === messageContent && chat.idUser !== idCRM)
      .map(chat => chat.idUser)
      .filter((id): id is number => id !== null && id !== undefined);
    return Array.from(new Set(userIds));
  };

  const validateToken = async (token: string, idBudget: string) => {
    const budgetId = parseInt(idBudget);
    try {
      toast.loading(t('toast.loading'), { id: 'loading' });
      const responseToken = await axiosEstimates.get<{ idSupplier: number }>('/supplier-chat-validate-token', {
        headers: { 'x-supplier-token': token },
      });

      const idSupplier = responseToken.data.idSupplier;

      const response = await axiosEstimates.get<{ supplier: SupplierChat; config: Config }>(
        `/supplier-chat/${budgetId}/${idSupplier}`,
        { headers: { 'x-supplier-token': token } },
      );

      const supplier = response.data.supplier;

      setIdUsers(getUserIdsWithMessage(supplier, '¡Saludos!'));

      setConfig(response.data.config);

      setIdSupplier(idSupplier);
      setIdBudget(budgetId);
      setChat({
        chat: supplier.supplierChats.map(chat => ({
          message: chat.content,
          username: chat.username,
          senderId: chat.idUser,
          time: chat.created_at,
          isFile: chat.isFile,
          filename: chat.filename || '',
          url: chat.url || '',
        })),
        id: budgetId,
        unseenMsgs: 0,
        userId: supplier.id,
      });
    } catch (error) {
      setIdSupplier(null);
    }
    toast.dismiss();
  };

  useEffect(() => {
    const language = searchParams.get('lang') || window.localStorage.getItem('lang') || 'es';

    window.localStorage.setItem('lang', language);
    console.log('Language set to:', language);
    i18n.changeLanguage(language);
    validateToken(searchParams.get('token') || '', searchParams.get('idBudget') || '');
  }, []);

  useEffect(() => {
    // Verificar si el socket está conectado

    if (!socket.connected) {
      socket.connect();
    }

    socket.on('connect', () => {});

    socket.on('new-message-supplier:success', () => {
      validateToken(searchParams.get('token') || '', searchParams.get('idBudget') || '');
    });
    socket.on('new-file-supplier:success', () => {
      validateToken(searchParams.get('token') || '', searchParams.get('idBudget') || '');
    });

    return () => {
      socket.off('new-message-supplier:success');
      socket.off('new-file-supplier:success');
    };
  }, [idBudget]);

  const handleSendMsg = (e: SyntheticEvent) => {
    e.preventDefault();

    if (!idBudget) return null;
    if (!idSupplier) return null;

    const data = {
      idBudget,
      idUser: null,
      content: msg,
      idSupplier,
      idUsers,
      lang: i18n.language,
    };
    socket.emit('new-message-supplier', data);

    setMsg('');
  };

  const handleSendFile = async () => {
    if (file) {
      let filename;
      if ('name' in file) {
        // file es de tipo File
        filename = file.name;
      } else if ('fileName' in file) {
        // file es de tipo fileBase64
        filename = file.fileName;
      }
      const data = {
        idBudget,
        idUser: null,
        idSupplier,
        file,
        idUsers,
        filename,
        lang: i18n.language,
      };

      socket.emit('new-file-supplier', data);
      setOpenFileUploader(prev => !prev);
      setFile(null);
    }
  };

  return {
    msg,
    handleSendMsg,
    setMsg,
    chat,
    open,
    handleToggle,
    idBudget,
    handleSendFile,
    config,
    openFileUploader,
    setOpenFileUploader,
    file,
    setFile,
  };
}
